import type { MoveProfile } from '@easymoving/types';
import type { ItemStats } from './itemStats';

export type SmartPromptKey =
  | 'setMoveDate'
  | 'needsDecision'
  | 'addPhotos'
  | 'firstBox'
  | 'packReady'
  | 'meterPhotos';

export interface SmartPrompt {
  key: SmartPromptKey;
  to: string;
  count?: number;
}

export interface SmartPromptContext {
  stats: ItemStats;
  boxCount: number;
  profile: MoveProfile | null;
  needsDecisionCount: number;
  /** Items with no uploaded photo (icon/placeholder only). */
  missingPhotoCount?: number;
  /** Injectable clock for the move-day window (defaults to now). */
  nowMs?: number;
}

const DAY = 86_400_000;

/** Move-day window: the day before through two days after the move. */
export function inMeterWindow(moveDate: number | undefined, nowMs: number): boolean {
  if (moveDate == null) return false;
  const days = Math.floor(moveDate / DAY) - Math.floor(nowMs / DAY);
  return days <= 1 && days >= -2;
}

/**
 * Contextual nudges for the dashboard (PRD §10.6). Pure: the same context
 * always yields the same prompts. Capped to keep the dashboard calm.
 */
export function generateSmartPrompts(ctx: SmartPromptContext): SmartPrompt[] {
  const out: SmartPrompt[] = [];
  if (ctx.stats.total === 0) return out; // empty app: handled by the start state

  // Move-day first: photograph the meters while you're standing next to them
  // (feeds the electricity/water account transfers — see the checklist guides).
  if (inMeterWindow(ctx.profile?.moveDate, ctx.nowMs ?? Date.now()))
    out.push({ key: 'meterPhotos', to: '/app/settle-in' });

  if (!ctx.profile?.moveDate) out.push({ key: 'setMoveDate', to: '/app/move' });
  if (ctx.needsDecisionCount > 0)
    out.push({ key: 'needsDecision', to: '/app/needs', count: ctx.needsDecisionCount });
  if ((ctx.missingPhotoCount ?? 0) > 0)
    out.push({ key: 'addPhotos', to: '/app/items?missing=photo', count: ctx.missingPhotoCount });
  if (ctx.boxCount === 0) out.push({ key: 'firstBox', to: '/app/boxes' });
  if (ctx.boxCount > 0 && ctx.stats.byProgress.ready_to_pack > 0)
    out.push({ key: 'packReady', to: '/app/boxes', count: ctx.stats.byProgress.ready_to_pack });

  return out.slice(0, 3);
}
